import React, { useEffect, useState, useRef } from 'react';
import WaveSurfer from 'wavesurfer.js';
// eslint-disable-next-line
// @ts-ignore
import CursorPlugin from 'wavesurfer.js/dist/plugin/wavesurfer.cursor';

import { getTailwindColor } from '@trqk/utils';
import { LoadingEllipses } from '..';

import { PlayPauseButton } from './components/PlayPauseButton';

interface Props {
    url: string;
    height?: number;
    width?: number;
}

const tertiary300 = getTailwindColor('tertiary-300');
const tertiary400 = getTailwindColor('tertiary-400');
const quarternary200 = getTailwindColor('quarternary-200');
const quarternary500 = getTailwindColor('quarternary-500');
const white = getTailwindColor('white');

export const AudioPlayer = ({
    url,
    height = 150,
    width = 600,
}: Props): JSX.Element => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [isReady, setIsReady] = useState(false);
    const waveformRef = useRef<WaveSurfer | null>(null);
    const containerElRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (containerElRef.current && !waveformRef.current) {
            waveformRef.current = WaveSurfer.create({
                barGap: 5,
                barRadius: 2,
                barWidth: 2,
                container: containerElRef.current,
                cursorColor: tertiary400,
                height,
                progressColor: quarternary500,
                waveColor: quarternary200,
                plugins: [
                    CursorPlugin.create({
                        showTime: true,
                        opacity: 1,
                        color: tertiary400,
                        customShowTimeStyle: {
                            'background-color': tertiary300,
                            color: white,
                            padding: '2px',
                            'font-size': '10px',
                        },
                    }),
                ],
            });
            waveformRef.current.on('ready', (): void => {
                setIsReady(true);
            });
            waveformRef.current.load(url);
            waveformRef.current.on('pause', (): void => {
                setIsPlaying(false);
            });
            waveformRef.current.on('play', (): void => {
                setIsPlaying(true);
            });
        }

        return (): void => {
            if (waveformRef.current) {
                waveformRef.current.pause();
            }
        };
    }, [containerElRef, waveformRef]);

    const playPauseButtonWidth = 40;
    const leftColWidth = playPauseButtonWidth + 10;
    const rightColWidth = width - leftColWidth;

    // WaveSurfer's layout breaks if its container is a flex container, so we are using grid instead
    return (
        <div
            style={{
                display: 'grid',
                gridTemplateColumns: `${leftColWidth}px auto`,
                gridTemplateRows: `${height}px`,
            }}
        >
            <PlayPauseButton
                width={playPauseButtonWidth}
                isPlaying={isPlaying}
                handleClick={(): void => {
                    if (waveformRef.current) {
                        waveformRef.current.playPause();
                    }
                }}
            />
            <div
                ref={containerElRef}
                className="relative"
                style={{ height, width: rightColWidth }}
            >
                {!isReady && (
                    <div
                        className="ml-4 italic text-tertiary-300 absolute"
                        style={{
                            top: Math.floor(height / 2) - 12,
                        }}
                    >
                        <LoadingEllipses fillColor="secondary-400" width={50} />
                    </div>
                )}
            </div>
        </div>
    );
};
